// Session lifecycle outside React: what the http client's 401 handler and the
// sign-out button both need.
import { clearDrafts } from "@/shared/lib/drafts";
import { getStytch, SESSION_DURATION_MINUTES } from "./client";

import type { StytchClient } from "./client";
import type { AuthMode } from "./types";

const RETURN_TO = "auth:return_to";

/** Where to land once signed in: kept across the trip to Stytch and back, in localStorage
    because an email link opens in a new tab. */
export function rememberReturnPath(path: string): void {
  try {
    localStorage.setItem(RETURN_TO, path);
  } catch {
    // no storage: they land on "/" instead
  }
}

/** The path kept by `rememberReturnPath`, used up; "/" when there is none. Only a path on
    this origin, so a planted value can't send anyone elsewhere. */
export function takeReturnPath(): string {
  let path: string | null = null;
  try {
    path = localStorage.getItem(RETURN_TO);
    localStorage.removeItem(RETURN_TO);
  } catch {
    // no storage: nothing was kept
  }
  return path && path.startsWith("/") && !path.startsWith("//") && !path.startsWith("/\\") ? path : "/";
}

/** The marketing site's sign-in page for `mode` (SignInOnSite), coming back to this origin's
    page kept by `rememberReturnPath` (used up). A failed callback's `error` and `reason` ride
    along, so the site can say what went wrong. */
export function siteSignInUrl(site: string, mode: AuthMode, params: URLSearchParams): string {
  const url = new URL(`/${mode}`, site);
  url.searchParams.set("next", window.location.origin + takeReturnPath());
  for (const key of ["error", "reason"]) {
    const value = params.get(key);
    if (value) url.searchParams.set(key, value);
  }
  return url.toString();
}

/** The bearer token for `shared/api/client.ts`. Read from the SDK, never stored. */
export const sessionJwt = (client: StytchClient = getStytch()): string | null =>
  client.session.getTokens()?.session_jwt ?? null;

/**
 * One 401 recovery attempt: re-mint the session JWT and let the request retry.
 * A dead session is the end of the road — revoke it and go to `/login`.
 *
 * `window.location` rather than react-router: this runs from an axios
 * interceptor, where no router context exists, and a full reload is the right
 * thing anyway when the session just died.
 */
export async function recoverSession(client: StytchClient = getStytch()): Promise<boolean> {
  if (!client.session.getSync()) return false;
  try {
    await client.session.authenticate({ session_duration_minutes: SESSION_DURATION_MINUTES });
    return true;
  } catch (e) {
    // The session is gone, not merely stale. Logged because it also explains
    // the redirect the user is about to be handed with no other warning.
    console.warn("session re-mint failed, signing out", e);
    await signOut(client);
    return false;
  }
}

/** Revoke and land on `/login`, coming back here once signed in again. Full reload — see
    `recoverSession`. */
export async function signOut(client: StytchClient = getStytch()): Promise<void> {
  // A revoke that fails changes nothing we would do differently — we are leaving
  // for /login either way, and the session dies with the page.
  await client.session.revoke().catch(() => undefined);
  clearDrafts(); // what this person typed and didn't send stays theirs
  rememberReturnPath(window.location.pathname + window.location.search);
  window.location.assign("/login");
}
